import { usePrincipalKey, useSessionKey } from "@auth/sessionStore";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import {
  Button,
  Checkbox,
  EmptyNote,
  Input,
  PanelError,
  SegmentedControl,
  Select,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "@utils/use-debounced-value";
import { useState } from "react";

import {
  JEV_QUESTIONS,
  type JevCallFilters,
  questionLabel,
} from "./jevCallClient";
import { JevCallDetail } from "./JevCallDetail";
import { useJevCallPage } from "./jevCallQueries";
import { JevTicksPanel } from "./JevTicksPanel";
import { clinicClock, duration } from "./traceFormat";
import type { JevCallSummary } from "./types";

const QUESTION_OPTIONS = [
  { value: "", label: "All questions" },
  ...JEV_QUESTIONS,
];

type Choices = Omit<JevCallFilters, "search">;

type View = "call" | "tick";
const VIEW_OPTIONS: { value: View; label: string }[] = [
  { value: "call", label: "By call" },
  { value: "tick", label: "By tick" },
];

const NO_CHOICES: Choices = {
  roomUuid: "",
  question: "",
  failed: false,
  boxFinding: false,
};

const topLine = (call: JevCallSummary): string =>
  call.error
    ? `Failed · ${call.error}`
    : Object.entries(call.top)
        .map(([name, top]) => `${name}: ${top}`)
        .join(" · ") || "No answer recorded";

const CallRow = ({
  call,
  timezone,
  expanded,
  active,
  onToggle,
}: {
  call: JevCallSummary;
  timezone: string;
  expanded: boolean;
  active: boolean;
  onToggle: () => void;
}) => (
  <li className="border-b border-rule last:border-b-0" data-dd-privacy="hidden">
    <button
      type="button"
      aria-expanded={expanded}
      onClick={onToggle}
      className="grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left hover:bg-smudge focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent sm:grid-cols-[auto_minmax(9rem,1fr)_minmax(8rem,1fr)_minmax(12rem,3fr)_auto] sm:gap-4"
    >
      <span aria-hidden="true" className="font-mono text-sm text-annotation">
        {expanded ? "⌄" : "›"}
      </span>
      <time
        dateTime={call.ticked_at}
        className="block text-mono-time text-xs text-ink-body"
      >
        {clinicClock(call.ticked_at, timezone)}
      </time>
      <span className="hidden min-w-0 sm:block">
        <span className="block truncate text-sm font-medium text-ink">
          {questionLabel(call.question)}
        </span>
        <span className="mt-1 block truncate text-xs text-annotation">
          {call.room_name}
        </span>
      </span>
      <span
        className={`hidden truncate font-mono text-xs sm:block ${call.error ? "text-ink" : "text-ink-body"}`}
      >
        {topLine(call)}
      </span>
      <span className="text-right font-mono text-xs text-annotation">
        {duration(call.ms)}
      </span>
      <span className="col-span-2 col-start-2 block truncate text-xs text-ink-body sm:hidden">
        {questionLabel(call.question)} · {call.room_name} · {topLine(call)}
      </span>
    </button>
    {expanded && <JevCallDetail callUuid={call.uuid} active={active} />}
  </li>
);

export const JevCallsPanel = ({ active }: { active: boolean }) => {
  const principal = usePrincipalKey();
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>("call");
  const [refreshes, setRefreshes] = useState(0);
  const [choices, setChoices] = useState<Choices>(NO_CHOICES);
  const [searchText, setSearchText] = useState("");
  const search = useDebouncedValue(searchText.trim(), 300);
  const filters: JevCallFilters = { ...choices, search };
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const goTo = (next: (string | undefined)[]) => {
    setCursors(next);
    setExpanded(new Set());
  };
  // A new search starts over from the first page, set during render.
  const [searched, setSearched] = useState(search);
  if (search !== searched) {
    setSearched(search);
    goTo([undefined]);
  }
  const choose = (change: Partial<Choices>) => {
    setChoices((current) => ({ ...current, ...change }));
    goTo([undefined]);
  };
  const byCall = view === "call";
  const query = useJevCallPage(
    active && byCall,
    filters,
    cursors[cursors.length - 1],
  );
  const rooms = useRoomsListQuery(useSessionKey(), undefined, active);
  const rows = query.data?.items ?? [];
  const timezone = query.data?.clinic_timezone || "UTC";
  const roomOptions = [
    { value: "", label: "All rooms" },
    ...(rooms.data ?? []).map((room) => ({ value: room.id, label: room.name })),
  ];
  const toggle = (uuid: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  const nextCursor = query.data?.next_cursor;
  const busy = !active || (byCall && query.isFetching);

  return (
    <section
      className="min-w-0 space-y-4 font-body text-ink-body"
      data-dd-privacy="hidden"
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-ink">Jev calls</h2>
          <p className="mt-1 text-sm text-annotation">
            {byCall
              ? "Recent questions the room cameras asked Jev, with what Jev was sent and what it answered, newest first."
              : "Each room tick's Jev calls together, with which boxes were kept or dropped and why, newest first."}
            {byCall && ` Times shown in ${timezone}.`}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => {
            goTo([undefined]);
            setRefreshes((n) => n + 1);
            void queryClient.invalidateQueries({
              queryKey: ["jev-calls", principal, byCall ? "list" : "ticks"],
            });
          }}
        >
          Refresh
        </Button>
      </div>
      <SegmentedControl
        aria-label="Jev calls view"
        options={VIEW_OPTIONS}
        value={view}
        onValueChange={setView}
        size="sm"
      />
      <div className="flex max-w-3xl flex-wrap items-end gap-3">
        <div className={byCall ? "min-w-48 flex-1" : "hidden"}>
          <Input
            label="Search calls"
            placeholder="Visit or slot id, request id, words from the talk"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>
        <div className="w-40">
          <Select
            label="Room"
            value={choices.roomUuid}
            options={roomOptions}
            onValueChange={(roomUuid) => choose({ roomUuid })}
          />
        </div>
        <div className={byCall ? "w-44" : "hidden"}>
          <Select
            label="Question"
            value={choices.question}
            options={QUESTION_OPTIONS}
            onValueChange={(question) => choose({ question })}
          />
        </div>
        {byCall && (
          <Checkbox
            label="Failed only"
            checked={choices.failed}
            onChange={(event) => choose({ failed: event.target.checked })}
          />
        )}
        {byCall && !choices.question && (
          <Checkbox
            label="Show box-finding calls"
            checked={choices.boxFinding}
            onChange={(event) => choose({ boxFinding: event.target.checked })}
          />
        )}
      </div>
      {!byCall && (
        <JevTicksPanel
          key={`${choices.roomUuid}:${refreshes}`}
          active={active}
          roomUuid={choices.roomUuid}
        />
      )}
      <div
        hidden={!byCall}
        className="overflow-hidden rounded-paper border border-rule bg-card"
      >
        <div className="hidden grid-cols-[auto_minmax(9rem,1fr)_minmax(8rem,1fr)_minmax(12rem,3fr)_auto] gap-4 border-b border-rule px-4 py-2 text-annotation-caps text-annotation sm:grid">
          <span />
          <span>Time</span>
          <span>Question · room</span>
          <span>Top answers</span>
          <span>Took</span>
        </div>
        {query.isPending && <SkeletonStack count={5} className="p-5" />}
        {query.isError && (
          <PanelError
            label="Jev calls"
            onRetry={() => void query.refetch()}
            className="min-h-48"
          />
        )}
        {query.data && rows.length === 0 && (
          <EmptyNote>No recorded Jev calls match this view.</EmptyNote>
        )}
        {rows.length > 0 && (
          <ul>
            {rows.map((call) => (
              <CallRow
                key={call.uuid}
                call={call}
                timezone={timezone}
                expanded={expanded.has(call.uuid)}
                active={active}
                onToggle={() => toggle(call.uuid)}
              />
            ))}
          </ul>
        )}
      </div>
      {byCall && query.data && (
        <nav className="flex items-center gap-3" aria-label="Jev call pages">
          <Button
            variant="outline"
            size="sm"
            disabled={busy || cursors.length === 1}
            onClick={() => goTo(cursors.slice(0, -1))}
          >
            Previous
          </Button>
          <span className="text-sm text-ink-body">Page {cursors.length}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={busy || !nextCursor}
            onClick={() => nextCursor && goTo([...cursors, nextCursor])}
          >
            Next
          </Button>
        </nav>
      )}
    </section>
  );
};
